// 샘플데이터 생성 화면
// 좌(30%): 기준사용자·복사계좌·계좌명매핑·축소비율·생성버튼
// 우(70%): 15단계 처리현황 + 실시간 로그
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSourceUserAccounts, getSampleConfig } from '../../api/sampleData';
import './SampleDataPage.css';

const SOURCE_USER_ID = 'user1';

const STEPS = [
  { key: 'delete',             desc: '기존 데이터 삭제',    table: '–' },
  { key: 'accounts',           desc: '계좌',                table: 'tb_accounts' },
  { key: 'stock_master',       desc: '종목마스터',           table: 'tb_stock_master' },
  { key: 'code_master',        desc: '공통코드',             table: 'tb_code_master' },
  { key: 'buy_history',        desc: '매수이력',             table: 'tb_buy_history' },
  { key: 'sell_history',       desc: '매도이력',             table: 'tb_sell_history' },
  { key: 'dividend',           desc: '분배금',               table: 'tb_dividend' },
  { key: 'balance_history',    desc: '현금성잔액',           table: 'tb_balance_history' },
  { key: 'invest_plan_sector', desc: '투자계획(섹터)',       table: 'tb_invest_plan_sector' },
  { key: 'invest_plan_stock',  desc: '투자계획(종목)',       table: 'tb_invest_plan_stock' },
  { key: 'alert_type',         desc: '알림유형',             table: 'tb_alert_type' },
  { key: 'buy_alert',          desc: '매수알림',             table: 'tb_buy_alert' },
  { key: 'sell_alert',         desc: '매도알림',             table: 'tb_sell_alert' },
  { key: 'alert_config',       desc: '시장지수알림',         table: 'tb_alert_config' },
  { key: 'reclose',            desc: '재집계 시작월 설정',   table: 'tb_system_config' },
];

// ── 상태 아이콘 ──────────────────────────────────────────
const StatusIcon = ({ status }) => {
  if (status === 'start') return <span className="sd-icon-running" />;
  if (status === 'done')  return <span className="sd-icon-done">✅</span>;
  if (status === 'error') return <span className="sd-icon-err">❌</span>;
  return <span className="sd-icon-wait">–</span>;
};

// ── 확인 팝업 ────────────────────────────────────────────
const ConfirmModal = ({ info, onOk, onCancel }) => (
  <div className="sd-overlay">
    <div className="sd-popup">
      <div className="sd-popup-hd">샘플 데이터 생성 확인</div>
      <div className="sd-popup-bd">
        <table className="sd-confirm-table">
          <tbody>
            <tr>
              <th>기준 사용자</th>
              <td>{info.sourceUserId}</td>
            </tr>
            <tr>
              <th>복사 계좌</th>
              <td>
                {info.accounts.map(a => (
                  <div key={a} className="sd-confirm-acct-row">
                    <span>{a}</span>
                    {info.accountNameMap[a] !== a && (
                      <span className="sd-confirm-arrow"> → <b>{info.accountNameMap[a]}</b></span>
                    )}
                  </div>
                ))}
              </td>
            </tr>
            <tr>
              <th>축소 비율</th>
              <td>{Math.round(info.ratio * 100)}%</td>
            </tr>
          </tbody>
        </table>
        <p className="sd-confirm-warn">
          ⚠ 기존 sample_user1 데이터가 모두 삭제됩니다.<br />
          계속 진행하시겠습니까?
        </p>
      </div>
      <div className="sd-popup-ft">
        <button className="sd-btn sd-btn-outline" onClick={onCancel}>취소</button>
        <button className="sd-btn sd-btn-primary" onClick={onOk}>생성 시작</button>
      </div>
    </div>
  </div>
);

// ── 완료 팝업 ────────────────────────────────────────────
const CompleteModal = ({ info, onClose }) => (
  <div className="sd-overlay">
    <div className="sd-popup sd-popup-sm">
      <div className="sd-popup-hd">완료</div>
      <div className="sd-popup-bd sd-complete-bd">
        <div className="sd-complete-icon">✅</div>
        <p className="sd-complete-msg">샘플 데이터 생성이 완료되었습니다!</p>
        {info.total > 0 && (
          <p className="sd-complete-total">총 {info.total.toLocaleString()}건 복사</p>
        )}
      </div>
      <div className="sd-popup-ft">
        <button className="sd-btn sd-btn-primary" style={{ minWidth: 100 }} onClick={onClose}>
          확인
        </button>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════
const SampleDataPage = () => {
  const [initLoading, setInitLoading] = useState(true);

  // 설정
  const [accounts, setAccounts]                 = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountNameMap, setAccountNameMap]     = useState({});
  const [ratio, setRatio]                       = useState('0.1');

  // 진행
  const [running, setRunning]       = useState(false);
  const [stepStatus, setStepStatus] = useState({});
  const [logs, setLogs]             = useState([]);
  const [errorMsg, setErrorMsg]     = useState('');

  // 팝업
  const [showConfirm, setShowConfirm]   = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [completeInfo, setCompleteInfo] = useState({ total: 0 });

  const logEndRef = useRef(null);

  // 초기 로딩
  useEffect(() => {
    (async () => {
      try {
        const [acctData, cfgData] = await Promise.all([
          getSourceUserAccounts(SOURCE_USER_ID),
          getSampleConfig(),
        ]);
        setAccounts(acctData.accounts || []);
        setRatio(String(cfgData.sample_ratio ?? 0.1));
      } catch (e) {
        setErrorMsg('초기화 오류: ' + (e.response?.data?.detail || e.message || '서버 오류'));
      } finally {
        setInitLoading(false);
      }
    })();
  }, []);

  // selectedAccounts 변경 시 accountNameMap 동기화
  useEffect(() => {
    setAccountNameMap(prev => {
      const next = {};
      for (const acct of selectedAccounts) {
        next[acct] = prev[acct] ?? acct;
      }
      return next;
    });
  }, [selectedAccounts]);

  // 로그 자동 스크롤
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const toggleAccount = useCallback((acct) => {
    setSelectedAccounts(prev =>
      prev.includes(acct) ? prev.filter(a => a !== acct) : [...prev, acct]
    );
  }, []);

  const selectAll = () => setSelectedAccounts([...accounts]);
  const clearAll  = () => setSelectedAccounts([]);

  // 생성 버튼 클릭
  const handleGenerateClick = () => {
    const r = parseFloat(ratio);
    if (isNaN(r) || r <= 0 || r > 1) {
      setErrorMsg('축소 비율은 0 초과 1 이하 소수로 입력하세요. (예: 0.1)');
      return;
    }
    if (selectedAccounts.length === 0) {
      setErrorMsg('복사할 계좌를 하나 이상 선택하세요.');
      return;
    }
    setErrorMsg('');
    setShowConfirm(true);
  };

  // SSE 스트리밍 실행
  const runGenerate = async () => {
    setShowConfirm(false);
    setRunning(true);
    setStepStatus({});
    setLogs([]);
    setErrorMsg('');

    const token = localStorage.getItem('token');
    const r     = parseFloat(ratio);

    const addLog  = (msg)              => setLogs(prev => [...prev, msg]);
    const setStep = (key, status, cnt) =>
      setStepStatus(prev => ({ ...prev, [key]: { status, count: cnt ?? 0 } }));

    let hasError    = false;
    let hasComplete = false;
    let finalTotal  = 0;

    try {
      const response = await fetch('/api/sample-data/generate', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          source_user_id:   SOURCE_USER_ID,
          accounts:         selectedAccounts,
          account_name_map: accountNameMap,
          ratio:            r,
        }),
      });

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try { detail = (await response.json()).detail || detail; } catch {}
        throw new Error(detail);
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        streamDone = done;
        if (value) buffer += decoder.decode(value, { stream: !done });

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }

          if (evt.type === 'step') {
            setStep(evt.key, evt.status, evt.count);
            if (evt.status === 'error') {
              hasError = true;
              setErrorMsg(evt.message || '오류 발생');
              addLog(`❌ [${evt.key}] ${evt.message || '오류'}`);
            }
          } else if (evt.type === 'log') {
            addLog(evt.message);
          } else if (evt.type === 'complete') {
            hasComplete = true;
            finalTotal  = evt.total ?? 0;
          } else if (evt.type === 'error') {
            hasError = true;
            setErrorMsg(prev => prev || (evt.message || '오류 발생'));
          }
        }
      }

      if (!hasError) {
        if (hasComplete) {
          setCompleteInfo({ total: finalTotal });
          setShowComplete(true);
        } else {
          setErrorMsg('연결이 예기치 않게 종료되었습니다.');
        }
      }

    } catch (e) {
      setErrorMsg(e.message || '생성 중 오류가 발생했습니다.');
      addLog(`❌ ${e.message || '생성 중 오류'}`);
    } finally {
      setRunning(false);
    }
  };

  const canGenerate = selectedAccounts.length > 0 && !running;
  const ratioNum    = parseFloat(ratio);
  const ratioLabel  = !isNaN(ratioNum) ? `(${Math.round(ratioNum * 100)}%)` : '';

  if (initLoading) return <div className="sd-init-loading">초기화 중...</div>;

  return (
    <div className="sd-split">

      {/* ════════ 좌측 패널 ════════ */}
      <div className="sd-panel sd-panel-left">

        {/* 기준 정보 */}
        <div className="sd-setting-card">
          <div className="sd-row">
            <span className="sd-label">기준 사용자</span>
            <span className="sd-fixed-val">user1 (일반사용자)</span>
          </div>
          <div className="sd-row">
            <span className="sd-label">샘플 계정</span>
            <span className="sd-fixed-val">sample_user1</span>
          </div>
        </div>

        {/* 복사할 계좌 */}
        <div className="sd-card">
          <div className="sd-card-header">
            <span>복사할 계좌</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="sd-mini-btn" onClick={selectAll} disabled={running}>전체</button>
              <button className="sd-mini-btn" onClick={clearAll}  disabled={running}>해제</button>
            </div>
          </div>
          <div className="sd-card-body">
            {accounts.length === 0 ? (
              <p className="sd-empty-hint">계좌가 없습니다.</p>
            ) : (
              <div className="sd-acct-list">
                {accounts.map(acct => (
                  <label key={acct} className="sd-acct-item">
                    <input
                      type="checkbox"
                      checked={selectedAccounts.includes(acct)}
                      onChange={() => toggleAccount(acct)}
                      disabled={running}
                    />
                    <span>{acct}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 계좌명 매핑 */}
        {selectedAccounts.length > 0 && (
          <div className="sd-card">
            <div className="sd-card-header">계좌명 매핑</div>
            <div className="sd-card-body">
              <table className="sd-map-table">
                <thead>
                  <tr>
                    <th>원래 계좌명</th>
                    <th>샘플 계좌명</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAccounts.map(acct => (
                    <tr key={acct}>
                      <td className="sd-map-orig">{acct}</td>
                      <td>
                        <input
                          type="text"
                          className="sd-map-input"
                          value={accountNameMap[acct] ?? acct}
                          onChange={e =>
                            setAccountNameMap(prev => ({ ...prev, [acct]: e.target.value }))
                          }
                          disabled={running}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 축소 비율 */}
        <div className="sd-setting-card">
          <div className="sd-row">
            <span className="sd-label">축소 비율</span>
            <input
              type="number"
              className="sd-input-num"
              value={ratio}
              onChange={e => setRatio(e.target.value)}
              step="0.01"
              min="0.01"
              max="1"
              disabled={running}
            />
            <span className="sd-ratio-hint">{ratioLabel}</span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>
            금액·수량에 비율을 곱해 생성 (단가·수익률은 그대로)
          </p>
        </div>

        {/* 생성 버튼 */}
        <div className="sd-action-area">
          {errorMsg && <p className="sd-error-msg">{errorMsg}</p>}
          <p className="sd-warn">⚠ 기존 sample_user1 데이터가 모두 삭제됩니다</p>
          <button
            className="sd-btn sd-btn-primary sd-gen-btn"
            onClick={handleGenerateClick}
            disabled={!canGenerate}
          >
            {running && <span className="sd-spinner" />}
            {running ? '생성 중...' : '샘플 데이터 생성'}
          </button>
        </div>

      </div>

      {/* 구분선 */}
      <div className="sd-resizer" />

      {/* ════════ 우측 패널 ════════ */}
      <div className="sd-panel sd-panel-right">

        {/* 처리 현황 */}
        <div className="sd-card">
          <div className="sd-card-header">처리 현황</div>
          <div className="sd-card-body" style={{ padding: '6px 12px' }}>
            <table className="sd-progress-table">
              <thead>
                <tr>
                  <th className="sd-pt-no">#</th>
                  <th className="sd-pt-desc">구분</th>
                  <th className="sd-pt-table">테이블</th>
                  <th className="sd-pt-cnt">건수</th>
                  <th className="sd-pt-stat">상태</th>
                </tr>
              </thead>
              <tbody>
                {STEPS.map((step, idx) => {
                  const st = stepStatus[step.key];
                  return (
                    <tr key={step.key} className={st?.status === 'error' ? 'sd-pt-row-err' : ''}>
                      <td className="sd-pt-no">{idx + 1}</td>
                      <td className="sd-pt-desc">{step.desc}</td>
                      <td className="sd-pt-table"><code>{step.table}</code></td>
                      <td className="sd-pt-cnt">
                        {st?.status === 'done' && st.count > 0
                          ? st.count.toLocaleString() : ''}
                      </td>
                      <td className="sd-pt-stat">
                        <StatusIcon status={st?.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 진행 로그 */}
        <div className="sd-card sd-card-grow">
          <div className="sd-card-header">진행 로그</div>
          <div className="sd-card-body sd-log-area">
            {logs.length === 0 ? (
              <p className="sd-log-empty">생성 버튼을 클릭하면 진행 로그가 표시됩니다.</p>
            ) : (
              logs.map((line, i) => (
                <div key={i} className="sd-log-line">{line}</div>
              ))
            )}
            <div ref={logEndRef} />
          </div>
        </div>

      </div>

      {/* 확인 팝업 */}
      {showConfirm && (
        <ConfirmModal
          info={{
            sourceUserId:   SOURCE_USER_ID,
            accounts:       selectedAccounts,
            accountNameMap: accountNameMap,
            ratio:          parseFloat(ratio),
          }}
          onOk={runGenerate}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* 완료 팝업 */}
      {showComplete && (
        <CompleteModal
          info={completeInfo}
          onClose={() => setShowComplete(false)}
        />
      )}
    </div>
  );
};

export default SampleDataPage;
